import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Paper,
  Stack,
  Typography,
  TextField,
  Button,
  MenuItem,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  TablePagination,
  Tooltip,
  CircularProgress,
  Alert,
  Divider,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Visibility as VisibilityIcon,
  Search as SearchIcon,
  History as HistoryIcon,
} from '@mui/icons-material';
import api from '../../services/api';
import AppShell from '../../layout/AppShell';

const actorRoles = [
  { value: '', label: 'All Roles' },
  { value: 'admin', label: 'Admin' },
  { value: 'seller', label: 'Seller' },
  { value: 'dlp', label: 'DLP' },
];

const entityTypes = [
  { value: '', label: 'All Entities' },
  { value: 'admin', label: 'Admin' },
  { value: 'seller', label: 'Seller' },
  { value: 'dlp', label: 'DLP' },
  { value: 'order', label: 'Order' },
  { value: 'payment', label: 'Payment' },
];

const actionTypes = [
  { value: '', label: 'All Actions' },
  { value: 'ADMIN_LOGIN', label: 'Admin Login' },
  { value: 'SELLER_REGISTER', label: 'Seller Register' },
  { value: 'SELLER_LOGIN', label: 'Seller Login' },
  { value: 'DLP_REGISTER', label: 'DLP Register' },
  { value: 'DLP_LOGIN', label: 'DLP Login' },
  { value: 'APPROVE_SELLER', label: 'Approve Seller' },
  { value: 'BLOCK_SELLER', label: 'Block Seller' },
  { value: 'UNBLOCK_SELLER', label: 'Unblock Seller' },
  { value: 'APPROVE_DLP', label: 'Approve DLP' },
  { value: 'CREATE_ORDER', label: 'Create Order' },
  { value: 'ASSIGN_DLP', label: 'Assign DLP' },
  { value: 'UPDATE_ORDER_STATUS', label: 'Admin Update Order Status' },
  { value: 'DLP_UPDATE_ORDER_STATUS', label: 'DLP Update Order Status' },
  { value: 'DISPATCH_ORDER_BY_BARCODE', label: 'Dispatch By Barcode' },
  { value: 'SCAN_INBOUND', label: 'Scan Inbound' },
  { value: 'SCAN_SORT', label: 'Scan Sort' },
  { value: 'MARK_COD_COLLECTED', label: 'Mark COD Collected' },
  { value: 'MARK_BANK_DEPOSITED', label: 'Mark Bank Deposited' },
];

function formatDate(value) {
  if (!value) return '-';

  try {
    return new Date(value).toLocaleString();
  } catch (error) {
    return value;
  }
}

function formatAction(action) {
  if (!action) return '-';
  return String(action).replaceAll('_', ' ');
}

function roleColor(role) {
  if (role === 'admin') return 'error';
  if (role === 'seller') return 'primary';
  if (role === 'dlp') return 'success';
  return 'default';
}

function actionColor(action) {
  if (!action) return 'default';

  if (String(action).includes('LOGIN')) return 'info';
  if (String(action).includes('BLOCK')) return 'error';
  if (String(action).includes('APPROVE')) return 'success';
  if (String(action).includes('ORDER')) return 'warning';
  if (String(action).includes('SCAN')) return 'secondary';

  return 'default';
}

function safeJson(value) {
  if (!value) return {};

  if (typeof value === 'object') {
    return value;
  }

  try {
    return JSON.parse(value);
  } catch (error) {
    return value;
  }
}

export default function AuditLogs() {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);

  const [search, setSearch] = useState('');
  const [actorRole, setActorRole] = useState('');
  const [entityType, setEntityType] = useState('');
  const [action, setAction] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [selectedLog, setSelectedLog] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);

  const summary = useMemo(() => {
    const adminCount = logs.filter((item) => item.actor_role === 'admin').length;
    const sellerCount = logs.filter((item) => item.actor_role === 'seller').length;
    const dlpCount = logs.filter((item) => item.actor_role === 'dlp').length;

    return {
      showing: logs.length,
      adminCount,
      sellerCount,
      dlpCount,
    };
  }, [logs]);

  const fetchAuditLogs = async () => {
    try {
      setLoading(true);
      setError('');

      const response = await api.get('/admin/audit-logs', {
        params: {
          page: page + 1,
          pageSize,
          q: search || undefined,
          actorRole: actorRole || undefined,
          entityType: entityType || undefined,
          action: action || undefined,
          fromDate: fromDate || undefined,
          toDate: toDate || undefined,
        },
      });

      const payload = response.data?.data || {};
      const items = payload.items || payload.logs || [];

      setLogs(items);
      setTotal(payload.total || items.length || 0);
    } catch (err) {
      console.error('Audit logs load error', err);

      setError(
        err.response?.data?.message ||
          'Failed to load audit logs. Please check backend API.'
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
  fetchAuditLogs();
}, [page, pageSize]);

  const handleSearch = () => {
    setPage(0);
    fetchAuditLogs();
  };

  const handleReset = () => {
    setSearch('');
    setActorRole('');
    setEntityType('');
    setAction('');
    setFromDate('');
    setToDate('');
    setPage(0);

    setTimeout(() => {
      fetchAuditLogs();
    }, 100);
  };

  const handleViewDetails = (log) => {
    setSelectedLog(log);
    setDetailsOpen(true);
  };

  return (
    <AppShell role="admin" title="Sellers">
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', md: 'center' }}
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Box>
          <Stack direction="row" spacing={1.2} alignItems="center">
            <Box
              sx={{
                width: 42,
                height: 42,
                borderRadius: 2,
                display: 'grid',
                placeItems: 'center',
                bgcolor: '#eef2ff',
                color: '#3730a3',
              }}
            >
              <HistoryIcon />
            </Box>

            <Box>
              <Typography variant="h5" fontWeight={800}>
                Audit Logs
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Track admin, seller, DLP, order, and payment activities
              </Typography>
            </Box>
          </Stack>
        </Box>

        <Button
          variant="contained"
          startIcon={<RefreshIcon />}
          onClick={fetchAuditLogs}
          disabled={loading}
          sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700 }}
        >
          Refresh
        </Button>
      </Stack>

      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Paper
          elevation={0}
          sx={{
            p: 2,
            flex: 1,
            border: '1px solid #e5e7eb',
            borderRadius: 3,
          }}
        >
          <Typography variant="body2" color="text.secondary">
            Total Logs
          </Typography>
          <Typography variant="h5" fontWeight={800}>
            {total}
          </Typography>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            p: 2,
            flex: 1,
            border: '1px solid #e5e7eb',
            borderRadius: 3,
          }}
        >
          <Typography variant="body2" color="text.secondary">
            Showing
          </Typography>
          <Typography variant="h5" fontWeight={800}>
            {summary.showing}
          </Typography>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            p: 2,
            flex: 1,
            border: '1px solid #e5e7eb',
            borderRadius: 3,
          }}
        >
          <Typography variant="body2" color="text.secondary">
            Admin Actions
          </Typography>
          <Typography variant="h5" fontWeight={800}>
            {summary.adminCount}
          </Typography>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            p: 2,
            flex: 1,
            border: '1px solid #e5e7eb',
            borderRadius: 3,
          }}
        >
          <Typography variant="body2" color="text.secondary">
            Seller / DLP Actions
          </Typography>
          <Typography variant="h5" fontWeight={800}>
            {summary.sellerCount + summary.dlpCount}
          </Typography>
        </Paper>
      </Stack>

      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 3,
          border: '1px solid #e5e7eb',
          borderRadius: 3,
        }}
      >
        <Stack spacing={2}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <TextField
              fullWidth
              size="small"
              label="Search"
              placeholder="Search action, entity, metadata..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
              }}
            />

            <TextField
              select
              size="small"
              label="Actor Role"
              value={actorRole}
              onChange={(e) => setActorRole(e.target.value)}
              sx={{ minWidth: { xs: '100%', md: 170 } }}
            >
              {actorRoles.map((item) => (
                <MenuItem key={item.value} value={item.value}>
                  {item.label}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              select
              size="small"
              label="Entity Type"
              value={entityType}
              onChange={(e) => setEntityType(e.target.value)}
              sx={{ minWidth: { xs: '100%', md: 170 } }}
            >
              {entityTypes.map((item) => (
                <MenuItem key={item.value} value={item.value}>
                  {item.label}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              select
              size="small"
              label="Action"
              value={action}
              onChange={(e) => setAction(e.target.value)}
              sx={{ minWidth: { xs: '100%', md: 230 } }}
            >
              {actionTypes.map((item) => (
                <MenuItem key={item.value} value={item.value}>
                  {item.label}
                </MenuItem>
              ))}
            </TextField>
          </Stack>

          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <TextField
              size="small"
              type="date"
              label="From Date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: { xs: '100%', md: 190 } }}
            />

            <TextField
              size="small"
              type="date"
              label="To Date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: { xs: '100%', md: 190 } }}
            />

            <Button
              variant="contained"
              onClick={handleSearch}
              disabled={loading}
              sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700 }}
            >
              Search
            </Button>

            <Button
              variant="outlined"
              onClick={handleReset}
              disabled={loading}
              sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700 }}
            >
              Reset
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      <Paper
        elevation={0}
        sx={{
          border: '1px solid #e5e7eb',
          borderRadius: 3,
          overflow: 'hidden',
        }}
      >
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: '#f9fafb' }}>
                <TableCell sx={{ fontWeight: 800 }}>Time</TableCell>
                <TableCell sx={{ fontWeight: 800 }}>Actor</TableCell>
                <TableCell sx={{ fontWeight: 800 }}>Action</TableCell>
                <TableCell sx={{ fontWeight: 800 }}>Entity</TableCell>
                <TableCell sx={{ fontWeight: 800 }}>IP Address</TableCell>
                <TableCell align="right" sx={{ fontWeight: 800 }}>
                  Details
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                    <CircularProgress size={28} />
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      Loading audit logs...
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : logs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                    <Typography fontWeight={700}>No audit logs found</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Try another filter or perform an admin/seller/DLP action.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                logs.map((log) => (
                  <TableRow key={log.id} hover>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      <Typography variant="body2" fontWeight={600}>
                        {formatDate(log.created_at)}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Stack spacing={0.5}>
                        <Chip
                          label={log.actor_role || '-'}
                          color={roleColor(log.actor_role)}
                          size="small"
                          sx={{ width: 'fit-content', fontWeight: 700 }}
                        />
                        <Typography variant="caption" color="text.secondary">
                          ID: {log.actor_id || '-'}
                        </Typography>
                      </Stack>
                    </TableCell>

                    <TableCell>
                      <Chip
                        label={formatAction(log.action)}
                        color={actionColor(log.action)}
                        size="small"
                        sx={{ fontWeight: 700 }}
                      />
                    </TableCell>

                    <TableCell>
                      <Typography variant="body2" fontWeight={700}>
                        {log.entity_type || '-'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        ID: {log.entity_id || '-'}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Typography variant="body2">
                        {log.ip_address || '-'}
                      </Typography>
                    </TableCell>

                    <TableCell align="right">
                      <Tooltip title="View details">
                        <IconButton onClick={() => handleViewDetails(log)}>
                          <VisibilityIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={total}
          page={page}
          rowsPerPage={pageSize}
          rowsPerPageOptions={[10, 20, 50, 100]}
          onPageChange={(event, newPage) => setPage(newPage)}
          onRowsPerPageChange={(event) => {
            setPageSize(Number(event.target.value));
            setPage(0);
          }}
        />
      </Paper>

      <Dialog
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 800 }}>
          Audit Log Details
        </DialogTitle>

        <DialogContent dividers>
          {selectedLog && (
            <Stack spacing={2}>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    Time
                  </Typography>
                  <Typography fontWeight={700}>
                    {formatDate(selectedLog.created_at)}
                  </Typography>
                </Box>

                <Box sx={{ flex: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    Actor
                  </Typography>
                  <Typography fontWeight={700}>
                    {selectedLog.actor_role} #{selectedLog.actor_id}
                  </Typography>
                </Box>

                <Box sx={{ flex: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    IP Address
                  </Typography>
                  <Typography fontWeight={700}>
                    {selectedLog.ip_address || '-'}
                  </Typography>
                </Box>
              </Stack>

              <Divider />

              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    Action
                  </Typography>
                  <Typography fontWeight={800}>
                    {formatAction(selectedLog.action)}
                  </Typography>
                </Box>

                <Box sx={{ flex: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    Entity
                  </Typography>
                  <Typography fontWeight={800}>
                    {selectedLog.entity_type} #{selectedLog.entity_id}
                  </Typography>
                </Box>
              </Stack>

              <Divider />

              <Box>
                <Typography variant="caption" color="text.secondary">
                  User Agent
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    mt: 0.5,
                    p: 1.5,
                    bgcolor: '#f9fafb',
                    borderRadius: 2,
                    border: '1px solid #e5e7eb',
                    wordBreak: 'break-word',
                  }}
                >
                  {selectedLog.user_agent || '-'}
                </Typography>
              </Box>

              <Box>
                <Typography variant="caption" color="text.secondary">
                  Metadata
                </Typography>
                <Box
                  component="pre"
                  sx={{
                    mt: 0.5,
                    p: 2,
                    bgcolor: '#111827',
                    color: '#f9fafb',
                    borderRadius: 2,
                    overflow: 'auto',
                    fontSize: 13,
                    maxHeight: 320,
                  }}
                >
                  {JSON.stringify(safeJson(selectedLog.metadata), null, 2)}
                </Box>
              </Box>
            </Stack>
          )}
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => setDetailsOpen(false)}
            sx={{ textTransform: 'none', fontWeight: 700 }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
    </AppShell>
  );
}